import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { partyColumns } from "../../utils/table/locationPartyCols";
import CustomTable from "../common/CustomTable";
import { addParty, editParty, getParties } from "../../api/inventory";
import MessageContainer from "../common/MessageContainer";
import { Fragment, useEffect, useState } from "react";
import { PartyEntryDetails } from "../../interfaces/app_types";
import { useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import {
  locationPartyEditIdState,
  locationPartyEditState,
} from "../../utils/atoms/atoms";
import FormField from "../common/FormField";
import { partyValidationSchema } from "../../utils/validations/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import InputSearchableDropdown from "./InputSearchableDropdown";
import { partyTypeValues } from "../../utils/dropdown-values";

const PartyModalBody = () => {
  let label = "";
  let color = "";
  const party_type = { value: "client", label: "Client" };
  const {
    isLoading,
    data: parties,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ["parties"],
    queryFn: getParties,
  });
  const resolver = yupResolver(partyValidationSchema);

  const [addBtnName, setAddBtnName] = useState("Add");

  const defaultValues = {
    label: label || "",
    color: color || "#000000",
    party_type: party_type || {},
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<PartyEntryDetails>({
    defaultValues,
    resolver,
  });

  const [disabledField, setDisabledField] = useState(true);
  const id = useRecoilValue(locationPartyEditIdState);
  const editState = useRecoilValue(locationPartyEditState);

  const resetFields = () => {
    reset({
      label: label || "",
      color: color || "#000000",
      party_type: party_type || "client",
    });
  };

  useEffect(() => {
    if (editState === "party") {
      const selectedParty = parties.filter((party) => {
        return party.value == id;
      })[0];

      label = selectedParty.label;
      color = selectedParty.color;
      party_type.value = selectedParty.party_type;
      party_type.label =
        selectedParty.party_type.charAt(0).toUpperCase() +
        selectedParty.party_type.slice(1);
      resetFields();
      setDisabledField(false);
      setAddBtnName("Edit");
    }
  }, [id, editState]);

  const queryClient = useQueryClient();

  const editPartyMutation = useMutation({
    mutationFn: async (data: any) => {
      return editParty(id, data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["parties"]);
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventory-vendor"]);

      setDisabledField(true);
      setAddBtnName("Add");
      resetFields();
    },
  });

  const addPartyMutation = useMutation({
    mutationFn: async (data: any) => {
      return addParty(data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["parties"]);
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventory-vendor"]);

      setDisabledField(true);
      resetFields();
    },
  });

  return isLoading ? (
    <MessageContainer message={"Loading"} />
  ) : isSuccess && !isError ? (
    <Fragment>
      <form
        action=""
        onSubmit={handleSubmit((data, event) => {
          event?.preventDefault();
          if (addBtnName === "Edit") {
            editPartyMutation.mutate(data);
          } else {
            addPartyMutation.mutate(data);
          }
        })}
      >
        <div className="d-flex gap-1 justify-content-between pb-2">
          <div className="d-flex col-8 flex-wrap">
            {addBtnName === "Edit" ? (
              <FormField
                title="ID"
                value={id}
                fieldType="display"
                disabled={true}
              />
            ) : (
              ""
            )}
            <FormField
              title="Name"
              value={label}
              disabled={disabledField}
              errorMessage={errors?.label?.message}
              formRegister={{ ...register("label") }}
            />
            <FormField
              title="Color"
              value={color}
              fieldType="color"
              disabled={disabledField}
              errorMessage={errors?.color?.message}
              formRegister={{ ...register("color") }}
            />
            <div className="col-12">
              <InputSearchableDropdown
                name="party_type"
                label="Party Type"
                selectedValue={party_type}
                options={partyTypeValues}
                control={control}
                disabled={disabledField}
                errorMessage={errors?.party_type?.message}
              />
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <button
              className="styled-btn--std me-2"
              disabled={!disabledField}
              onClick={() => {
                setDisabledField(false);
              }}
            >
              {addBtnName}
            </button>
            <button
              className="styled-btn--std me-2"
              type="submit"
              disabled={disabledField}
            >
              Submit
            </button>
            <button
              className="styled-btn--std me-2"
              disabled={disabledField}
              onClick={() => {
                setDisabledField(true);
                setAddBtnName("Add");
                resetFields();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
      <CustomTable data={parties} columns={partyColumns} />
    </Fragment>
  ) : (
    ""
  );
};

export default PartyModalBody;
